/**
 * @file QueueProviderDistribution.tsx
 * @description Componente que exibe a distribuição de tarefas da fila por provider em gráfico de pizza
 * 
 * ATUALIZADO v2.0.0: Migrado para usar hook PGMQ (useFlowQueueByProviderPgmq) 
 * em vez da RPC legada get_queue_provider_distribution que consultava tabela vazia
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { useFlowQueueByProviderPgmq } from '@/hooks/useFlowQueueMonitorPgmq';

/**
 * Cores distintas para cada provider
 * Inclui waha que é o provider mais utilizado atualmente
 */
const PROVIDER_COLORS: Record<string, string> = {
  'z-api': '#22c55e',
  'evolution': '#3b82f6',
  'chatwoot': '#f59e0b',
  'wuzapi': '#8b5cf6',
  'notificame': '#ec4899',
  'waha': '#10b981',
  'unknown': '#6b7280',
  'desconhecido': '#6b7280',
};

/**
 * Componente de distribuição por provider usando dados PGMQ
 */
export function QueueProviderDistribution() {
  // Usa hook PGMQ que consulta as filas corretas (pgmq.q_flows e pgmq.a_flows)
  const { data: providerData, isLoading } = useFlowQueueByProviderPgmq();

  // Converte dados do hook para formato esperado pelo gráfico
  const providerStats = (providerData || [])
    .filter(item => item.count > 0)
    .map(item => ({
      provider: item.provider_slug === 'unknown' ? 'Desconhecido' : item.provider_slug.toUpperCase(),
      count: item.count,
    }));

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Distribuição por Provider</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-[300px]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!providerStats || providerStats.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Distribuição por Provider</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-[300px]">
          <p className="text-sm text-muted-foreground">Nenhum dado disponível</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Distribuição por Provider</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={providerStats}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ provider, percent }) => `${provider} (${(percent * 100).toFixed(0)}%)`}
              outerRadius={80}
              fill="#8884d8"
              dataKey="count"
            >
              {providerStats.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={PROVIDER_COLORS[entry.provider.toLowerCase()] || PROVIDER_COLORS.unknown}
                />
              ))}
            </Pie>
            <Tooltip 
              formatter={(value: number) => [`${value} tarefas`, 'Total']}
            />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
